import { useMemo } from 'react';
import { FormConfig, GenericFormReturn, SubmitOperationConfig, useGenericForm } from '../../../common/hooks/useGenericForm';

export interface StoreEditFormData {
    storeName: string;
}

/**
 * Custom hook for managing store edit form state and validation.
 * 
 * Handles store name validation and form state management for updating
 * existing store information.
 * 
 * @param storeName - Initial store name value
 * @param submitOperationConfig - Configuration for form submission handling
 * @returns Form state and methods for store editing
 */
export const useStoreEditForm = (
    storeName: string,
    submitOperationConfig: SubmitOperationConfig,
): GenericFormReturn<StoreEditFormData> => {
    const formConfig: FormConfig<StoreEditFormData> = useMemo(() => ({
        submitOperationConfig,
        fields: [
            {
                name: 'storeName',
                required: true,
                validate: (value: StoreEditFormData['storeName']) => {
                    if (typeof value !== 'string' || value.trim() === '') {
                        return { isValid: false, statusMessage: 'Store name is required.' };
                    }
                    return { isValid: true };
                },
            },
        ],
    }), [submitOperationConfig]);

    const initialValues: StoreEditFormData = useMemo(() => ({ storeName }), [storeName]);

    return useGenericForm<StoreEditFormData>({ initialValues, formConfig });
};
